// ============================================================================
// AdminDashboard — landing page for the admin app.
// ============================================================================
// Surfaces three things:
//   1. Stat cards — upcoming events, recent registrations, room bookings,
//      items needing attention. Pulled from existing /api/events + /api/admin
//      /registrations (no new endpoints).
//   2. Quick actions — Add event, Bulk upload, Materialize series, Print QR.
//      Same routes the top bar exposes but more discoverable for new users.
//   3. Recent activity — last 10 registrations across all events, with a
//      "View all" link to the Registrations panel.
//
// Designed to be the "what's happening today?" view. Not a deep analytics
// page — that's task #37 (Reports).
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import {
  Plus, Upload, Sparkles, QrCode, CalendarDays, Users, DoorOpen, AlertCircle,
  ArrowRight, RefreshCw,
} from 'lucide-react';
import { api, type AdminRegistration, type PublicEvent } from '../api';

interface Props {
  token: string;
  /** Bubble registration count up to AdminLayout so the sidebar badge stays accurate. */
  onRegistrationsCount?: (count: number) => void;
}

export function AdminDashboard({ token, onRegistrationsCount }: Props) {
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [registrations, setRegistrations] = useState<AdminRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [evRes, regRes] = await Promise.all([
        // includeDrafts so dashboard counts reflect everything the admin owns,
        // not just published events.
        api.events({ includePast: false, includeDrafts: true, token }),
        api.adminRegistrations(token),
      ]);
      setEvents(evRes.events);
      setRegistrations(regRes.registrations);
      onRegistrationsCount?.(regRes.registrations.filter((r) => r.registration_status === 'registered').length);
    } catch (err: any) {
      setError(err?.message || 'Could not load dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // ----- Derived stats -----
  const now = Date.now();
  const weekAhead = now + 7 * 24 * 60 * 60 * 1000;

  const upcomingEvents = events.filter((e) => new Date(e.start_date).getTime() >= now);
  const eventsThisWeek = upcomingEvents.filter((e) => new Date(e.start_date).getTime() <= weekAhead);
  const regsThisWeek = registrations.filter((r) => {
    const t = new Date(r.created_at).getTime();
    return t >= now - 7 * 24 * 60 * 60 * 1000;
  });

  const needsAttention = useMemo(() => {
    // Heuristics for the "Needs attention" stat:
    // 1. Events with registrations exceeding max_attendees (oversold).
    // 2. Events within 48h with 0 registrations.
    const reasons: string[] = [];
    for (const e of upcomingEvents) {
      const count = registrations
        .filter((r) => r.event_id === e.event_id && ['registered', 'attended'].includes(r.registration_status))
        .reduce((s, r) => s + (r.attendee_count || 1), 0);
      if (e.max_attendees != null && count > e.max_attendees) {
        reasons.push(`${e.title} is oversold (${count}/${e.max_attendees})`);
      }
      const hoursUntil = (new Date(e.start_date).getTime() - now) / (1000 * 60 * 60);
      if (hoursUntil > 0 && hoursUntil < 48 && count === 0) {
        reasons.push(`${e.title} starts in <48h with no registrations`);
      }
    }
    return reasons;
  }, [upcomingEvents, registrations, now]);

  const recent = [...registrations]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome back</h1>
          <p className="text-sm text-gray-600 mt-0.5">Here's what's happening this week.</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Upcoming events"
          value={upcomingEvents.length}
          sub={`${eventsThisWeek.length} this week`}
          icon={<CalendarDays className="h-4 w-4 text-uif-green-dark" />}
        />
        <StatCard
          label="Registrations this week"
          value={regsThisWeek.length}
          sub={`${registrations.length} total`}
          icon={<Users className="h-4 w-4 text-uif-green-dark" />}
        />
        <StatCard
          label="Room bookings"
          value={eventsThisWeek.filter((e) => e.resource_id != null).length}
          sub="Linked this week"
          icon={<DoorOpen className="h-4 w-4 text-uif-green-dark" />}
        />
        <StatCard
          label="Needs attention"
          value={needsAttention.length}
          sub={needsAttention.length === 0 ? 'All clear' : 'Tap to review'}
          icon={<AlertCircle className={`h-4 w-4 ${needsAttention.length > 0 ? 'text-amber-600' : 'text-gray-400'}`} />}
          accent={needsAttention.length > 0 ? 'amber' : undefined}
        />
      </div>

      {/* Needs attention details */}
      {needsAttention.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900 space-y-1">
              {needsAttention.map((r, i) => <div key={i}>· {r}</div>)}
            </div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Quick actions</div>
        <div className="flex flex-wrap gap-2">
          <QuickAction icon={<Plus className="h-4 w-4" />} label="Add event" primary
            onClick={() => (window.location.hash = '#admin/events/new')} />
          <QuickAction icon={<Upload className="h-4 w-4" />} label="Bulk upload"
            onClick={() => (window.location.hash = '#admin/events/bulk')} />
          <QuickAction icon={<Sparkles className="h-4 w-4" />} label="Materialize series"
            onClick={() => (window.location.hash = '#admin/series')} />
          <QuickAction icon={<QrCode className="h-4 w-4" />} label="View room calendar"
            onClick={() => window.open('/#agc', '_blank')} />
        </div>
      </div>

      {/* Recent registrations */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Recent registrations</div>
          <a href="#admin/registrations" className="text-xs font-medium text-uif-green hover:text-uif-green-dark inline-flex items-center gap-1">
            View all <ArrowRight className="h-3 w-3" />
          </a>
        </div>
        {loading ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">Loading…</div>
        ) : recent.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">No registrations yet.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recent.map((r) => (
              <div key={r.attendee_id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 truncate">{r.full_name}</div>
                  <div className="text-xs text-gray-500 truncate">{r.event_title}</div>
                </div>
                <div className="text-xs text-gray-500 ml-3 flex-shrink-0">
                  {new Date(r.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon, accent }: {
  label: string; value: number; sub?: string; icon: React.ReactNode; accent?: 'amber';
}) {
  return (
    <div className={
      'rounded-lg border p-4 ' +
      (accent === 'amber' ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white')
    }>
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-600">{label}</div>
      </div>
      <div className="text-2xl font-bold text-uif-green-dark">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function QuickAction({ icon, label, onClick, primary }: {
  icon: React.ReactNode; label: string; onClick: () => void; primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ' +
        (primary
          ? 'bg-uif-green text-white hover:bg-uif-green-dark'
          : 'border border-gray-300 text-gray-700 hover:bg-gray-50')
      }
    >
      {icon}
      {label}
    </button>
  );
}
